import { Trans, useLingui } from "@lingui/react/macro";
import { BOT_INSTRUCTIONS_MAX_LENGTH } from "@rakazo/contracts";
import { Button, NativeSelect, NativeSelectOption, Textarea } from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSpaceQueries } from "../lib/query";
import { useSpaceRpc } from "../lib/space-rpc";
import { errorText } from "../lib/user-error";
import { CompanyBotRole } from "./CompanyBotRole";

export function BotInstructionsPanel({ botId }: { botId: string }) {
  const queries = useSpaceQueries();
  const { t } = useLingui();
  const members = useQuery(queries.bots.configurationMembers.queryOptions({ input: { botId } }));
  const [selected, setSelected] = useState("");
  const userId = selected || members.data?.[0]?.userId;
  return (
    <div className="space-y-6">
      <CompanyBotRole botId={botId} />
      <section className="space-y-3">
        <h3 className="text-sm font-medium">
          <Trans>User guidance</Trans>
        </h3>
        <NativeSelect
          aria-label={t`User`}
          value={userId ?? ""}
          onChange={(e) => setSelected(e.target.value)}
        >
          {(members.data ?? []).map((member) => (
            <NativeSelectOption key={member.userId} value={member.userId}>
              {member.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        {userId ? <UserGuidance key={`${botId}:${userId}`} botId={botId} userId={userId} /> : null}
      </section>
    </div>
  );
}

function UserGuidance({ botId, userId }: { botId: string; userId: string }) {
  const { t } = useLingui();
  const queries = useSpaceQueries();
  const rpc = useSpaceRpc();
  const config = useQuery(
    queries.bots.privateInstructions.queryOptions({ input: { botId, userId } }),
  );
  const [draft, setDraft] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const value = draft ?? config.data?.instructions ?? "";
  async function save() {
    if (!config.data || pending || value === config.data.instructions) return;
    setPending(true);
    setError("");
    try {
      await rpc.bots.updatePrivateInstructions({
        botId,
        userId,
        instructions: value,
        expectedInstructions: config.data.instructions,
      });
      setDraft(null);
      await config.refetch();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPending(false);
    }
  }
  return (
    <div className="space-y-3">
      <Textarea
        aria-label={t`Private user guidance`}
        disabled={config.isPending || pending || config.isError}
        value={value}
        maxLength={BOT_INSTRUCTIONS_MAX_LENGTH}
        onChange={(e) => setDraft(e.target.value)}
        className="min-h-48 field-sizing-content"
      />
      {error || config.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {error || t`Could not load guidance.`}
        </p>
      ) : null}
      <Button
        disabled={pending || !config.data || value === config.data.instructions}
        onClick={() => void save()}
      >
        <Trans>Save guidance</Trans>
      </Button>
    </div>
  );
}
